import { configureStore } from '@reduxjs/toolkit'
import chatUxSlice from '@/chat-common/store/chat-ux.slice'
import aiQueryResultSlice from './slices/ai-query-result.slice'
import globalToastSlice from './slices/global-toast.slice'
import uiStateSlice from './slices/ui-state.slice'
import { paxtonAppApi } from './apis/paxton-app-api'
import { getSourceApi } from '@/chat-common/api/source-apis'
import { hostedFiltersApi } from './apis/hosted-filters-api'
import { orgAdminApi } from '@/organizations/apis/org-admin.api'
import citatorStateSlice from '@/citator/store/citator-state.slice'
import checkoutReducer, { checkoutApi } from '@/checkout/store/slice'
import appInitSlice from './slices/app-init.slice'
import uploadTasksSlice from './slices/file-upload-tasks.slice'
import selectedFilesSlice from '@/chat-common/selected_files/store/slice'
import { selectedFilesListenerMiddleware } from '@/chat-common/selected_files/store/listeners'
import { chatUxListenerMiddleware } from '@/chat-common/store/chat-ux.listeners'
import agentConversationsSlice from '@/agent/conversations/store/slice'
import ws2Slice from '@/websocket_v2/ws2.slice'
import agentConversationFormSlice from '@/agent/chat_form/store/slice'
import { agentFormListenerMiddleware } from '@/agent/chat_form/store/listeners'
import { ws2ListenerMiddleware } from '@/websocket_v2/ws2.listeners'
import artifactsSlice from '@/artifacts/store/slice'
import artifactTemplatesSlice from '@/artifact-templates/store/slice'
import agentEventsSlice from '@/agent/events/store/slice'
import agentResponseMetadataSlice from '@/agent/response_metadata/store/slice'
import { agentConversationsListenerMiddleware } from '@/agent/conversations/store/listeners'
import { artifactListenerMiddleware } from '@/artifacts/store/listeners'
import agentReferencesSlice from '@/agent/references/store/slice'
import contentViewerSlice from '@/content-viewer/store/slice'
import draftingSlice from '@/drafting/store/slice'
import { draftingListenerMiddleware } from '@/drafting/store/listeners'
import { toastListenerMiddleware } from './slices/toast-middleware'
import mattersSlice from '@/matter/store/slice'
import { mattersListenerMiddleware } from '@/matter/store/listeners'
import conversationNavigationSlice from '@/chat-common/conversation-navigation/store/slice'
import skillsSlice from '@/skills/store/slice'
import { skillsListenerMiddleware } from '@/skills/store/listeners'
import responseFeedbackSlice from '@/response-feedback/store/slice'
import { responseFeedbackListenerMiddleware } from '@/response-feedback/store/listeners'
import accountReducer from '@/account/store/slice'
import { accountListenerMiddleware } from '@/account/store/listeners'
import medChronJobSlice from '@/medical-chronology/jobs/store/slice'
import { medChronListenerMiddleware } from '@/medical-chronology/jobs/store/listeners'

import { medSummaryEditListenerMiddleware } from '@/medical-billing-summaries/entries/store/entries-edit.listeners'
import medSummaryAssetSlice from '@/medical-billing-summaries/asset/store/slice'
import { medSummaryAssetListenerMiddleware } from '@/medical-billing-summaries/asset/store/listeners'
import { artifactGenerationAnalyticsMiddleware } from '@/analytics/store/listeners/artifact-generation-analytics.listener'
import templateAnalytics from '@/analytics/store/listeners/template-analytics.listener'
import riaAnalyticsListener from '@/analytics/store/listeners/ria-analytics.listener'
import { researchFiltersReducer } from '@/agent/chat_form/sources-dialog/store/slice'
import { researchFiltersListenerMiddleware } from '@/agent/chat_form/sources-dialog/store/listeners'
import medChronEventsSlice from '@/medical-chronology/events/store/slice'
import medChronReferencesSlice from '@/medical-chronology/references/store/slice'
import medChronAssetSlice from '@/medical-chronology/asset/store/slice'
import { medChronAssetListenerMiddleware } from '@/medical-chronology/asset/store/listeners'
import timelineEventsSlice from '@/timeline/events/store/slice'
import timelineReferencesSlice from '@/timeline/references/store/slice'
import timelineAssetSlice from '@/timeline/asset/store/slice'
import { timelineAssetListenerMiddleware } from '@/timeline/asset/store/listeners'
import uppyFileUploadSlice from '@/uppy-file-upload/store/slice'
import { uppyFileUploadListenerMiddleware } from '@/uppy-file-upload/store/listeners'
import userFoldersSlice from '@/user_files/folders/store/slice'
import { userFoldersListenerMiddleware } from '@/user_files/folders/store/listeners'
import userFilesSlice from '@/user_files/files/store/slice'
import { userFilesListenerMiddleware } from '@/user_files/files/store/listeners'
import userFilesPollingSlice from '@/user_files/polling/store/slice'
import { userFilesPollingListenerMiddleware } from '@/user_files/polling/store/listeners'
import userFilesBatchingSlice from '@/user_files/batching/store/slice'
import { userFilesBatchingListenerMiddleware } from '@/user_files/batching/store/listeners'
import externalStorageSlice from '@/user_files/external-storage/store/slice'
import medSummaryEntriesSlice from '@/medical-billing-summaries/entries/store/entries.slice'
import medSummaryEntryEditSlice from '@/medical-billing-summaries/entries/store/entries-edit.slice'
import medSummaryReferencesSlice from '@/medical-billing-summaries/references/store/slice'
import inlineEditSlice from '@/inline-edit/store/slice'
import { inlineEditListenerMiddleware } from '@/inline-edit/store/listeners'
import collaborationSlice from '@/collaboration/store/slice'
import { collaborationListenerMiddleware } from '@/collaboration/store/listeners'

export const store = configureStore({
  reducer: {
    agentConversationsState: agentConversationsSlice,
    agentConversationFormState: agentConversationFormSlice,
    researchFiltersState: researchFiltersReducer,
    agentEventsState: agentEventsSlice,
    agentResponseMetadataState: agentResponseMetadataSlice,
    agentReferencesState: agentReferencesSlice,
    artifactsState: artifactsSlice,
    artifactTemplatesState: artifactTemplatesSlice,
    contentViewerState: contentViewerSlice,
    draftingState: draftingSlice,
    chatUxState: chatUxSlice,
    selectedFilesState: selectedFilesSlice,
    uploadFileTasksState: uploadTasksSlice,
    aiQueryResults: aiQueryResultSlice,
    globalToast: globalToastSlice,
    uiState: uiStateSlice,
    appInitState: appInitSlice,
    citatorState: citatorStateSlice,
    medChronJobState: medChronJobSlice,
    medChronEventsState: medChronEventsSlice,
    medChronReferencesState: medChronReferencesSlice,
    medChronAssetState: medChronAssetSlice,
    timelineEventsState: timelineEventsSlice,
    timelineReferencesState: timelineReferencesSlice,
    timelineAssetState: timelineAssetSlice,
    medSummaryAssetState: medSummaryAssetSlice,
    medSummaryEntriesState: medSummaryEntriesSlice,
    medSummaryEntryEditState: medSummaryEntryEditSlice,
    medSummaryReferencesState: medSummaryReferencesSlice,
    uppyFileUploadState: uppyFileUploadSlice,
    userFoldersState: userFoldersSlice,
    userFilesState: userFilesSlice,
    userFilesPollingState: userFilesPollingSlice,
    userFilesBatchingState: userFilesBatchingSlice,
    externalStorageState: externalStorageSlice,
    skillsState: skillsSlice,
    responseFeedbackState: responseFeedbackSlice,
    checkoutState: checkoutReducer,
    ws2State: ws2Slice,
    mattersState: mattersSlice,
    conversationNavigationState: conversationNavigationSlice,
    accountState: accountReducer,
    inlineEditState: inlineEditSlice,
    collaborationState: collaborationSlice,
    [checkoutApi.reducerPath]: checkoutApi.reducer,
    // Add generated RTK Query reducers (the streaming endpoints are not supported by RTK Query)
    [paxtonAppApi.reducerPath]: paxtonAppApi.reducer, // THIS IS THE NEW RTK QUERY API FOR SUGGESTED QUESTIONS
    [getSourceApi.reducerPath]: getSourceApi.reducer,
    [hostedFiltersApi.reducerPath]: hostedFiltersApi.reducer,
    [orgAdminApi.reducerPath]: orgAdminApi.reducer,
  },

  // Add createListenerMiddleware and RTK-Query Middleware
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(paxtonAppApi.middleware, getSourceApi.middleware, hostedFiltersApi.middleware, orgAdminApi.middleware, checkoutApi.middleware)
      // Prepend listener middleware because it must be started before other middleware
      .prepend(
        artifactListenerMiddleware.middleware,
        ws2ListenerMiddleware.middleware,
        researchFiltersListenerMiddleware.middleware,
        agentFormListenerMiddleware.middleware,
        agentConversationsListenerMiddleware.middleware,
        mattersListenerMiddleware.middleware,
        draftingListenerMiddleware.middleware,
        chatUxListenerMiddleware.middleware,
        selectedFilesListenerMiddleware.middleware,
        toastListenerMiddleware.middleware,
        skillsListenerMiddleware.middleware,
        responseFeedbackListenerMiddleware.middleware,
        medChronListenerMiddleware.middleware,
        medChronAssetListenerMiddleware.middleware,
        timelineAssetListenerMiddleware.middleware,
        medSummaryEditListenerMiddleware.middleware,
        medSummaryAssetListenerMiddleware.middleware,
        accountListenerMiddleware.middleware,
        templateAnalytics.middleware,
        artifactGenerationAnalyticsMiddleware.middleware,
        riaAnalyticsListener.middleware,
        uppyFileUploadListenerMiddleware.middleware,
        userFoldersListenerMiddleware.middleware,
        userFilesListenerMiddleware.middleware,
        userFilesPollingListenerMiddleware.middleware,
        userFilesBatchingListenerMiddleware.middleware,
        inlineEditListenerMiddleware.middleware,
        collaborationListenerMiddleware.middleware
      ),
})
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch
